const assets = {
  moduleTitle: "Assets",
  nav: {
    assetsAndDevices: "Assets",
    assetTypes: "Asset Types",
    assets: "Assets",
  },
  actions: {
    view: "View",
    create: "Create",
    update: "Update",
    delete: "Delete",
  },
  permissions: {
    core: "Core",
    assetManagement: "Asset Management",
    assetTypes: "Asset Types",
    assets: "Assets",
  },
  assetTypes: {
    list: {
      title: "Asset Types",
      subtitle:
        "Create, search, and manage the asset types used across the system.",
    },
    stats: {
      total: "Total Asset Types",
      totalHint: "types registered",
      // Static placeholder until the backend adds a real isActive field --
      // see the TODO in AssetTypesListScreen.
      active: "Active Types",
      activeHint: "ready to use",
      inactive: "Inactive Types",
      inactiveHint: "temporarily disabled",
      loading: "Loading",
      error: "Unavailable",
    },
    toolbar: {
      add: "Add Asset Type",
      archive: "Archive",
      search: "Search by asset type name",
      searchPlaceholder: "Search...",
    },
    status: {
      active: "Active",
      inactive: "Inactive",
    },
    table: {
      name: "Type Name",
      company: "Company",
      fieldsCount: "Number of Fields",
      status: "Status",
      createdAt: "Created",
      archivedAt: "Archived Date",
      empty: "No asset types match this search.",
      error: "Asset types could not be loaded.",
      loading: "Loading asset types",
      showing: "Showing {{count}} of {{total}}",
      rowsPerPageLabel: "Rows per page",
      nextPage: "Next page",
      previousPage: "Previous page",
      pagination: "Pagination",
      sortAscending: "Sort ascending",
      sortDescending: "Sort descending",
      actionMenu: "Asset type actions",
    },
    actions: {
      view: "View",
      update: "Update",
      delete: "Delete",
      restore: "Restore",
    },
    filters: {
      archivedAt: "Archive date",
      archivedAtPlaceholder: "Select a date range",
      clearDateRange: "Clear date range",
    },
    datePicker: {
      nextMonth: "Next month",
      previousMonth: "Previous month",
    },
    deleteDialog: {
      title: "Delete",
      question: "Are you sure you want to delete this asset type?",
      warning: "This action is permanent and cannot be undone.",
      confirmWord: "DELETE",
      instruction: 'Type "{{word}}" to confirm deletion',
      inputLabel: "Deletion confirmation",
      cancel: "Cancel",
      confirm: "Delete",
      close: "Close delete dialog",
      successFallback: "Asset type deleted successfully.",
      errorFallback: "Failed to delete asset type. Please try again.",
    },
    archived: {
      title: "Archived Asset Types",
      subtitle: "Asset types that have been archived and can be restored.",
      empty: "No archived asset types match these filters.",
    },
    detail: {
      title: "Company Details",
      companyName: "Company Name",
      name: "Name",
      notAvailable: "Not available",
      backToList: "Back to asset types",
    },
    restoreDialog: {
      title: "Restore Asset Type",
      question: "Are you sure you want to restore this asset type?",
      prompt:
        "This asset type will become active again and can be used in the system.",
      cancel: "Cancel",
      confirm: "Restore Asset Type",
      close: "Close restore dialog",
      successFallback: "Asset type restored successfully.",
      errorFallback: "The asset type could not be restored.",
    },
    form: {
      title: "Add Asset Type",
      subtitle: "Basic profile details and field configuration.",
      createSuccessFallback: "Asset type created successfully.",
      updateSuccessFallback: "Asset type updated successfully.",
      editTitle: "Edit Asset Type",
      editSubtitle: "Update the asset type's details and add new fields.",
      loading: "Loading asset type",
      loadError: "The asset type could not be loaded.",
      empty: "This asset type could not be found.",
      generalInfo: "General Information",
      company: "Company",
      companyPlaceholder: "Choose a company",
      companySearch: "Search companies",
      companyEmpty: "No companies found.",
      nameAr: "Arabic Name",
      nameEn: "English Name",
      description: "Description",
      optional: "(optional)",
      fieldsSection: "Asset Fields",
      fieldNameAr: "Field Name (AR)",
      fieldNameEn: "Field Name (EN)",
      type: "Type",
      required: "Required",
      unique: "Unique",
      addField: "Add field",
      removeField: "Remove field",
      uploadDocuments: "Allow document upload for assets of this type",
      status: "Active",
      statusDescription: "Whether this asset type is currently in use.",
      toggleStatusDialog: {
        titleActivate: "Activate Asset Type",
        titleDeactivate: "Deactivate Asset Type",
        questionActivate: "Are you sure you want to activate this asset type?",
        questionDeactivate:
          "Are you sure you want to deactivate this asset type?",
        note: "This change won't be applied until you save.",
        yes: "Yes",
        no: "No",
        close: "Close status change dialog",
      },
      fieldType: {
        string: "Text",
        number: "Number",
        boolean: "Yes / No",
        date: "Date",
      },
      uniqueFieldRequired: "At least one field must be marked as unique.",
      duplicateFieldName: "Field name already exists.",
      fieldNameTooLong: "Must be {{count}} characters or fewer.",
      fieldAutoFillNote:
        "Existing assets of this type will be automatically filled with a default value for this field.",
      saveFailed: "The asset type could not be saved. Please try again.",
      save: "Save",
      cancel: "Cancel",
    },
  },
  asset: {
    list: {
      title: "Assets",
      subtitle:
        "Track all of the company's registered assets, their types, and the devices/cards linked to them, along with their actual operating status.",
    },
    toolbar: {
      add: "Add New Asset",
      archive: "Archive",
      search: "Search by asset name",
      searchPlaceholder: "Search...",
    },
    filters: {
      filters: "Filters",
      status: "Status",
      allStatuses: "All statuses",
      archivedAt: "Archive date",
      archivedAtPlaceholder: "Select a date range",
      clearDateRange: "Clear date range",
    },
    datePicker: {
      nextMonth: "Next month",
      previousMonth: "Previous month",
    },
    attachments: {
      mainInformation: "Main Information",
      tabLabel: "Attachments",
      addAttachments: "Add attachments",
      dropzoneTitle: "Drag files here or click to browse",
      dropzoneHint: "PDF, JPG, JPEG, PNG — up to 25 MB per file, 10 files max",
      browseFiles: "Browse files",
      addAnotherAttachment: "Add another attachment",
      addAttachment: "Add attachment",
      removeAttachment: "Remove attachment",
      attachmentPending: "Pending upload",
      fileSizeMb: "{{size}} MB",
      tooManyFiles: "You can upload up to {{count}} files.",
      invalidType: "Only PDF, JPG, JPEG, and PNG files are allowed.",
      tooLarge: "Each file must not exceed 25 MB.",
    },
    status: {
      active: "Active",
      inactive: "Inactive",
    },
    table: {
      name: "Asset Name",
      code: "Asset Code",
      type: "Asset Type",
      company: "Company",
      status: "Status",
      archivedAt: "Archived Date",
      empty: "No assets match this search.",
      error: "Assets could not be loaded.",
      loading: "Loading assets",
      showing: "Showing {{count}} of {{total}}",
      rowsPerPageLabel: "Rows per page",
      nextPage: "Next page",
      previousPage: "Previous page",
      pagination: "Pagination",
      sortAscending: "Sort ascending",
      sortDescending: "Sort descending",
      actionMenu: "Asset actions",
    },
    actions: {
      view: "View",
      update: "Update",
      delete: "Delete",
      restore: "Restore",
    },
    deleteDialog: {
      title: "Delete",
      question: "Are you sure you want to delete this asset?",
      warning: "This action is permanent and cannot be undone.",
      confirmWord: "DELETE",
      instruction: 'Type "{{word}}" to confirm deletion',
      inputLabel: "Deletion confirmation",
      cancel: "Cancel",
      confirm: "Delete",
      close: "Close delete dialog",
      successFallback: "Asset deleted successfully.",
      errorFallback: "Failed to delete asset. Please try again.",
    },
    archived: {
      title: "Archived Assets",
      back: "Back to assets",
      subtitle: "Assets that have been archived and can be restored.",
      empty: "No archived assets match these filters.",
    },
    restoreDialog: {
      title: "Restore Asset",
      question: "Are you sure you want to restore this asset?",
      prompt:
        "This asset will become active again and can be used in the system.",
      cancel: "Cancel",
      confirm: "Restore Asset",
      close: "Close restore dialog",
      successFallback: "Asset restored successfully.",
      errorFallback: "The asset could not be restored.",
    },
    form: {
      title: "Add Asset",
      subtitle: "Basic details and information for the file.",
      generalInfo: "General Information",
      branch: "Branch",
      branchPlaceholder: "Choose a branch",
      branchSearch: "Search branches",
      branchEmpty: "No branches found.",
      company: "Company",
      companyPlaceholder: "Choose a company",
      companySearch: "Search companies",
      companyEmpty: "No companies found.",
      assetType: "Asset Type",
      assetTypePlaceholder: "Choose an asset type",
      assetTypeSearch: "Search asset types",
      assetTypeEmpty: "No asset types found.",
      attributesSection: "Asset Details",
      optional: "(optional)",
      invalidNumber: "Enter a valid number.",
      tooShort: "Must be at least {{count}} characters.",
      tooLong: "Must be {{count}} characters or fewer.",
      invalidFormat: "Enter a value in the correct format.",
      loadingFields: "Loading the form for this asset type...",
      loadFormError: "Could not load the form for this asset type.",
      selectDatePlaceholder: "Select a date",
      nextMonth: "Next month",
      previousMonth: "Previous month",
      createSuccessFallback: "Asset created successfully.",
      saveFailed: "The asset could not be saved. Please try again.",
      save: "Save",
      cancel: "Cancel",
    },
    detail: {
      backToList: "Back to assets",
      generalInfo: "General Information",
      companyDetails: "Company Details",
      company: "Company",
      code: "Asset Code",
      branch: "Branch",
      assetType: "Asset Type",
      assetName: "Asset Name",
      nameEn: "Name (English)",
      nameAr: "Name (Arabic)",
      notes: "Notes",
      notAvailable: "Not available",
      unanswered: "Not answered",
      yes: "Yes",
      no: "No",
      attachments: "Attachments",
      noAttachments: "No attachments yet.",
      loading: "Loading asset",
      loadError: "Could not load this asset.",
      empty: "Could not find this asset.",
    },
    edit: {
      title: "Edit Asset",
      subtitle: "Update this asset's status and details.",
      backToDetail: "Back to asset details",
      status: "Active",
      statusDescription: "Toggle whether this asset is currently in service.",
      updateSuccessFallback: "Asset updated successfully.",
      updateFailed: "The asset could not be updated. Please try again.",
      statusDialog: {
        titleActivate: "Activate Asset",
        titleDeactivate: "Deactivate Asset",
        questionActivate: "Are you sure you want to activate this asset?",
        questionDeactivate: "Are you sure you want to deactivate this asset?",
        note: "This change won't be applied until you save.",
        yes: "Yes",
        no: "No",
        close: "Close status change dialog",
      },
    },
  },
};

export default assets;
